import type {
    Control,
    FieldErrors,
    FieldValues,
    Path,
    UseFormRegister,
} from 'react-hook-form';
import type { StackProps } from 'tamagui';

import type { InputProps } from '../inputs/Input';
import { PercentInput } from '../inputs/PercentInput';
import {
    ControlledFormattedInput,
    type FormatterFunction,
} from './ControlledFormattedInput';

const NON_NUMERIC_REGEX = /[^(\d*)]/g;
const PERCENT_SIGN_WITH_SPACE = ' %';

const createPercentageFormatter = (
    maxValue: number = 100,
): FormatterFunction => {
    return (input: string, previousValue?: string) => {
        // If input already ends with " %", it's already formatted
        if (input.endsWith(' %')) {
            return {
                displayValue: input,
                storedValue: input,
            };
        }

        // Remove anything other than digits (whole numbers only allowed)
        // Also remove the % and space if present
        const digitsOnly = input
            .replace(NON_NUMERIC_REGEX, '')
            .replace(/\s*%\s*/g, '');
        const numericValue = parseInt(digitsOnly, 10);

        if (!Number.isNaN(numericValue) && numericValue > maxValue) {
            // Don't allow percentage over maxValue
            return {
                displayValue: previousValue ?? '',
                storedValue: previousValue ?? '',
            };
        }

        if (digitsOnly === '') {
            // Return empty when empty
            return {
                displayValue: '',
                storedValue: '',
            };
        }

        // Return number with % postfix and space: "50 %"
        const formattedValue = `${digitsOnly}${PERCENT_SIGN_WITH_SPACE}`;
        return {
            displayValue: formattedValue,
            storedValue: formattedValue,
        };
    };
};

interface ControlledPercentInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends Omit<InputProps, 'formatValueOnChange'> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    maxValue?: number;
    label?: string;
    hint?: string;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledPercentInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    maxValue = 100,
    ...props
}: ControlledPercentInputProps<TFieldValues, TContext>) => {
    return (
        <ControlledFormattedInput
            {...props}
            formatter={createPercentageFormatter(maxValue)}
            InputComponent={PercentInput}
            inputComponentProps={{
                placeholder: props.placeholder ?? '%',
                maxValue,
            }}
        />
    );
};
